import React, { useEffect, useRef, useMemo, useState, useCallback } from "react";
import {
  ChevronUpIcon,
  ChevronDownIcon,
  MapIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
  ArrowPathIcon,
  ArrowDownTrayIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
// @ts-expect-error react-cytoscapejs has no official types
import CytoscapeComponent from "react-cytoscapejs";
import { Core, ElementDefinition } from "cytoscape";
import { NetworkData } from "../../hooks/useNetworkData";
import { PlotControlsDrawer } from "./PlotControlsDrawer";
import { useTheme } from "../../theme/useTheme";
import pathwayExplorerStyles from "../../modules/gene/PathwayExplorer.module.css";

interface DirectNeighborGraphProps {
  networkData: NetworkData | null;
  cytoscapeElements: ElementDefinition[];
  edgeType: string;
  isPlotControlsOpen: boolean;
  setIsPlotControlsOpen: (isOpen: boolean) => void;
  selectedGeneToHighlight: string;
  setSelectedGeneToHighlight: (gene: string) => void;
  highlightColumn: string;
  setHighlightColumn: (col: string) => void;
  datasetId: string;
  highlightedGenesList?: string[];
  labelFontSize: number;
  setLabelFontSize: (size: number) => void;
  cyRef: React.MutableRefObject<Core | null>;
  layoutInitialized: React.MutableRefObject<boolean>;
  selectedPathwayLabel?: string;
  splitComponents?: boolean;
  setSplitComponents?: (split: boolean) => void;
}

export const DirectNeighborGraph: React.FC<DirectNeighborGraphProps> = ({
  networkData,
  cytoscapeElements,
  edgeType: _edgeType,
  isPlotControlsOpen,
  setIsPlotControlsOpen,
  selectedGeneToHighlight,
  setSelectedGeneToHighlight,
  highlightColumn,
  setHighlightColumn,
  datasetId,
  highlightedGenesList = [],
  labelFontSize,
  setLabelFontSize,
  cyRef,
  layoutInitialized,
  selectedPathwayLabel = "",
  splitComponents = false,
  setSplitComponents,
}) => {
  const themeCtx = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const edgeTableFullscreenRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isEdgeTableFullscreen, setIsEdgeTableFullscreen] = useState(false);
  const [viewMode, setViewMode] = useState<"graph" | "table">("graph");
  const [edgeTableSearchQuery, setEdgeTableSearchQuery] = useState("");
  const [selectedNodeLabel, setSelectedNodeLabel] = useState<string | null>(null);
  const [connectedGenes, setConnectedGenes] = useState<string[]>([]);
  /** Tap-to-focus: cytoscape node id + 1-hop neighbor ids */
  const [tapFocusNodeId, setTapFocusNodeId] = useState<string | null>(null);
  const [tapNeighborIds, setTapNeighborIds] = useState<string[]>([]);
  const selectedNodeLabelRef = useRef<string | null>(null);
  const hasSyncedInitialFontSizeRef = useRef(false);
  useEffect(() => {
    selectedNodeLabelRef.current = selectedNodeLabel;
  }, [selectedNodeLabel]);

  // Reset so we only sync computed font size once per graph load (not on later layout/splitComponents)
  useEffect(() => {
    hasSyncedInitialFontSizeRef.current = false;
    setTapFocusNodeId(null);
    setTapNeighborIds([]);
  }, [cytoscapeElements]);

  // When switching back to graph view, resize and refit once the container is visible (avoids 0-size container)
  useEffect(() => {
    if (viewMode !== "graph" || !networkData?.nodes.length) return;
    const id = setTimeout(() => {
      const cy = cyRef.current;
      if (cy) {
        cy.resize();
        cy.fit(undefined, 30);
      }
    }, 150);
    return () => clearTimeout(id);
  }, [viewMode, networkData?.nodes.length, cyRef]);

  useEffect(() => {
    const onFullscreenChange = () => {
      const fullscreen = !!document.fullscreenElement;
      setIsFullscreen(fullscreen);
      // Resize and refit graph when container size changes (enter or exit fullscreen)
      requestAnimationFrame(() => {
        setTimeout(() => {
          const cy = cyRef.current;
          if (cy) {
            cy.resize();
            cy.fit(undefined, 30);
          }
          if (!fullscreen && containerRef.current) {
            containerRef.current.focus();
          }
        }, 100);
      });
    };
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, [cyRef]);

  const handleFullscreen = useCallback(() => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }, []);

  const handleReset = useCallback(() => {
    const cy = cyRef.current;
    if (!cy) return;
    const layout = cy.layout({
      name: "cose",
      animate: true,
      animationDuration: 800,
      nodeDimensionsIncludeLabels: true,
      idealEdgeLength: 60,
      gravity: 1,
      gravityRange: 2.5,
    } as any);
    layout.run();
    layout.on("layoutstop", () => {
      cy.fit(undefined, 30);
      cy.center();
    });
  }, [cyRef]);

  const handleDownload = useCallback(() => {
    const cy = cyRef.current;
    if (!cy) return;
    const png = cy.png({ output: "blob", scale: 2, bg: themeCtx.mode === "dark" ? "#1e293b" : "#f8fafc" });
    const url = URL.createObjectURL(png);
    const link = document.createElement("a");
    link.href = url;
    link.download = "network-graph.png";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [cyRef, themeCtx.mode]);

  useEffect(() => {
    const onFullscreenChange = () => {
      setIsEdgeTableFullscreen(document.fullscreenElement === edgeTableFullscreenRef.current);
    };
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  const filteredEdgeRows = useMemo(() => {
    if (!networkData?.edges) return [];
    const q = edgeTableSearchQuery.trim().toLowerCase();
    if (!q) return networkData.edges;
    return networkData.edges.filter((e) => {
      const source = String(e.source ?? "").toLowerCase();
      const target = String(e.target ?? "").toLowerCase();
      return source.includes(q) || target.includes(q);
    });
  }, [networkData?.edges, edgeTableSearchQuery]);

  const handleEdgeTableFullscreen = useCallback(() => {
    const el = edgeTableFullscreenRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }, []);

  const handleDownloadEdgeTableCsv = useCallback(() => {
    if (!networkData?.edges || networkData.edges.length === 0) return;
    const header = "Source,Target,Path,Path length\n";
    const escape = (v: string) => {
      if (v.includes(",") || v.includes('"') || v.includes("\n")) return `"${v.replace(/"/g, '""')}"`;
      return v;
    };
    const rows = networkData.edges.map(
      (e) =>
        `${escape(String(e.source ?? ""))},${escape(String(e.target ?? ""))},${escape(Array.isArray(e.path) ? e.path.join(" → ") : "")},${e.path_length ?? ""}`
    );
    const csv = header + rows.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "direct-neighbor-edges.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [networkData?.edges]);

  const cytoscapeStylesheet = useMemo(
    () => {
      const isDark = themeCtx.mode === "dark";
      /** Match KShortestPaths / Comparison gene palette */
      const geneCyanFill = isDark ? "#22d3ee" : "#06b6d4";
      const geneYellowFill = isDark ? "#fde047" : "#facc15";
      const edgeGeneCyanTint = isDark ? "#5eead4" : "#a5f3fc";
      const edgeGeneAmberTint = isDark ? "#fcd34d" : "#fde68a";
      const labelColor = isDark ? "#e2e8f0" : "#1e293b";
      const nodeSearchBorder = isDark ? "#ecfeff" : "#0e7490";
      const edgeSearchWidth = 2;
      const edgeCyanWidth = 1.2;
      const edgeAmberWidth = 0.9;
      const edgeTapSubgraphColor = edgeGeneCyanTint;
      const tapFocusBorderCenter = isDark ? "#f8fafc" : "#0f172a";
      const tapFocusBorderNeighbor = isDark ? "#94a3b8" : "#475569";

      const isSearchOrSelectedGene = (nodeLabel: string) =>
        selectedGeneToHighlight !== "none" && nodeLabel === selectedGeneToHighlight;
      const isHighlightColumnGene = (nodeLabel: string) =>
        highlightedGenesList.length > 0 && highlightedGenesList.includes(nodeLabel);

      const inTapNeighborhood = (nodeId: string) => {
        if (!tapFocusNodeId) return true;
        return nodeId === tapFocusNodeId || tapNeighborIds.includes(nodeId);
      };
      const isTapCenter = (nodeId: string) => tapFocusNodeId != null && nodeId === tapFocusNodeId;
      const edgeInTapNeighborhood = (ele: any) => {
        if (!tapFocusNodeId) return false;
        const s = ele.source().id();
        const t = ele.target().id();
        return inTapNeighborhood(s) && inTapNeighborhood(t);
      };
      const nodeKeepsFullOpacityWhenTapDim = (nodeLabel: string) =>
        isSearchOrSelectedGene(nodeLabel) || isHighlightColumnGene(nodeLabel);

      return [
        {
          selector: "node",
          style: {
            opacity: (ele: any) => {
              const nodeLabel = ele.data("label");
              if (!tapFocusNodeId || nodeKeepsFullOpacityWhenTapDim(nodeLabel)) return 1;
              return inTapNeighborhood(ele.id()) ? 1 : 0.22;
            },
            "background-color": (ele: any) => {
              const nodeLabel = ele.data("label");
              if (isSearchOrSelectedGene(nodeLabel)) return geneCyanFill;
              if (highlightedGenesList.includes(nodeLabel)) return geneYellowFill;
              return geneCyanFill;
            },
            "border-width": (ele: any) => {
              const nodeLabel = ele.data("label");
              if (isSearchOrSelectedGene(nodeLabel)) return 2;
              if (isHighlightColumnGene(nodeLabel)) return 2;
              if (tapFocusNodeId && inTapNeighborhood(ele.id())) return 1;
              return 0;
            },
            "border-color": (ele: any) => {
              const nodeLabel = ele.data("label");
              if (isSearchOrSelectedGene(nodeLabel)) return nodeSearchBorder;
              if (isHighlightColumnGene(nodeLabel)) return tapFocusBorderNeighbor;
              if (tapFocusNodeId && inTapNeighborhood(ele.id())) {
                return isTapCenter(ele.id()) ? tapFocusBorderCenter : tapFocusBorderNeighbor;
              }
              return geneCyanFill;
            },
            "border-opacity": 1,
            shape: "ellipse",
            "border-style": "solid",
            width: (ele: any) => {
              const nodeLabel = ele.data("label");
              if (isSearchOrSelectedGene(nodeLabel)) return 18;
              if (isHighlightColumnGene(nodeLabel)) return 18;
              if (tapFocusNodeId && isTapCenter(ele.id())) return 20;
              if (tapFocusNodeId && inTapNeighborhood(ele.id())) return 18;
              return 16;
            },
            height: (ele: any) => {
              const nodeLabel = ele.data("label");
              if (isSearchOrSelectedGene(nodeLabel)) return 18;
              if (isHighlightColumnGene(nodeLabel)) return 18;
              if (tapFocusNodeId && isTapCenter(ele.id())) return 20;
              if (tapFocusNodeId && inTapNeighborhood(ele.id())) return 18;
              return 16;
            },
            padding: 0,
            label: "data(label)",
            "text-valign": "bottom",
            "text-halign": "center",
            "text-margin-y": (ele: any) => {
              const nodeLabel = ele.data("label");
              const nodeH = isSearchOrSelectedGene(nodeLabel)
                ? 18
                : tapFocusNodeId && isTapCenter(ele.id())
                  ? 20
                  : tapFocusNodeId && inTapNeighborhood(ele.id())
                    ? 18
                    : isHighlightColumnGene(nodeLabel)
                      ? 18
                      : 16;
              const fs = Math.max(6, labelFontSize);
              const gapAboveNode = 6;
              const labelHeightApprox = Math.ceil(fs * 1.35);
              return -(Math.ceil(nodeH / 2) + labelHeightApprox + gapAboveNode);
            },
            color: labelColor,
            "font-size": `${Math.max(6, labelFontSize)}px`,
            "font-family": "system-ui, -apple-system, sans-serif",
            "font-weight": 400,
            "text-wrap": "wrap",
            "text-max-width": "80px",
          } as any,
        },
        {
          selector: "node:active",
          style: {
            "overlay-color": isDark ? "#64748b" : "#94a3b8",
            "overlay-padding": 8,
            "overlay-opacity": 0.2,
          } as any,
        },
        {
          selector: "node.dragging",
          style: {
            "border-width": 1.5,
            "border-color": isDark ? "#67e8f9" : "#0891b2",
            "background-color": isDark ? "#155e75" : "#cffafe",
          } as any,
        },
        {
          selector: "edge",
          style: {
            width: (ele: any) => {
              const srcLabel = ele.source().data("label");
              const tgtLabel = ele.target().data("label");
              const touchesSearchGene =
                selectedGeneToHighlight !== "none" &&
                (srcLabel === selectedGeneToHighlight || tgtLabel === selectedGeneToHighlight);
              const touchesHighlightColumn =
                highlightedGenesList.length > 0 &&
                (highlightedGenesList.includes(srcLabel) || highlightedGenesList.includes(tgtLabel));
              if (tapFocusNodeId) {
                if (edgeInTapNeighborhood(ele)) return 2.4;
                if (touchesSearchGene) return edgeSearchWidth;
                if (touchesHighlightColumn) return edgeCyanWidth;
                return 0.5;
              }
              if (touchesSearchGene) return edgeSearchWidth;
              if (touchesHighlightColumn) return edgeCyanWidth;
              return edgeAmberWidth;
            },
            "line-color": (ele: any) => {
              const srcLabel = ele.source().data("label");
              const tgtLabel = ele.target().data("label");
              const touchesSearchGene =
                selectedGeneToHighlight !== "none" &&
                (srcLabel === selectedGeneToHighlight || tgtLabel === selectedGeneToHighlight);
              const touchesHighlightColumn =
                highlightedGenesList.length > 0 &&
                (highlightedGenesList.includes(srcLabel) || highlightedGenesList.includes(tgtLabel));
              if (tapFocusNodeId) {
                if (edgeInTapNeighborhood(ele)) return edgeTapSubgraphColor;
                if (touchesSearchGene) return edgeGeneCyanTint;
                if (touchesHighlightColumn) return edgeGeneAmberTint;
                return isDark ? "#475569" : "#94a3b8";
              }
              if (touchesSearchGene) return edgeGeneCyanTint;
              if (touchesHighlightColumn) return edgeGeneAmberTint;
              return edgeGeneAmberTint;
            },
            "line-opacity": (ele: any) => {
              const srcLabel = ele.source().data("label");
              const tgtLabel = ele.target().data("label");
              const touchesSearchGene =
                selectedGeneToHighlight !== "none" &&
                (srcLabel === selectedGeneToHighlight || tgtLabel === selectedGeneToHighlight);
              const touchesHighlightColumn =
                highlightedGenesList.length > 0 &&
                (highlightedGenesList.includes(srcLabel) || highlightedGenesList.includes(tgtLabel));
              if (tapFocusNodeId) {
                if (edgeInTapNeighborhood(ele)) return 1;
                if (touchesSearchGene || touchesHighlightColumn) return 0.9;
                return 0.12;
              }
              if (touchesSearchGene || touchesHighlightColumn) return 0.9;
              return 0.75;
            },
            "target-arrow-shape": "none",
            "curve-style": "straight",
            "font-size": "8px",
            color: isDark ? "#94a3b8" : "#64748b",
          } as any,
        },
      ];
    },
    [
      labelFontSize,
      themeCtx.mode,
      selectedGeneToHighlight,
      highlightedGenesList,
      tapFocusNodeId,
      tapNeighborIds,
    ]
  );

  const components = useMemo(() => {
    if (!splitComponents || !networkData) return [cytoscapeElements];

    const nodes = cytoscapeElements.filter((el) => !el.data.source);
    const edges = cytoscapeElements.filter((el) => el.data.source);

    const adj = new Map<string, string[]>();
    nodes.forEach((n) => adj.set(n.data.id!, []));

    edges.forEach((e) => {
      const s = e.data.source!;
      const t = e.data.target!;
      if (adj.has(s)) adj.get(s)!.push(t);
      if (adj.has(t)) adj.get(t)!.push(s);
    });

    const visited = new Set<string>();
    const comps: ElementDefinition[][] = [];

    for (const node of nodes) {
      const nodeId = node.data.id!;
      if (!visited.has(nodeId)) {
        const compNodeIds = new Set<string>();
        const queue = [nodeId];
        visited.add(nodeId);
        compNodeIds.add(nodeId);

        while (queue.length > 0) {
          const curr = queue.shift()!;
          for (const neighbor of adj.get(curr) || []) {
            if (!visited.has(neighbor)) {
              visited.add(neighbor);
              compNodeIds.add(neighbor);
              queue.push(neighbor);
            }
          }
        }

        const compNodes = nodes.filter((n) => compNodeIds.has(n.data.id!));
        const compEdges = edges.filter(
          (e) => compNodeIds.has(e.data.source!) && compNodeIds.has(e.data.target!)
        );

        comps.push([...compNodes, ...compEdges]);
      }
    }

    comps.sort((a, b) => {
      const aNodesCount = a.filter((el) => !el.data.source).length;
      const bNodesCount = b.filter((el) => !el.data.source).length;
      return bNodesCount - aNodesCount;
    });

    if (comps.length <= 1) return comps;

    const largest = comps[0];
    const others = comps.slice(1).flat();

    return [largest, others];
  }, [splitComponents, cytoscapeElements, networkData]);

  const [activePlotIndex, setActivePlotIndex] = useState(0);

  useEffect(() => {
    layoutInitialized.current = false;
    if (!splitComponents) {
      setActivePlotIndex(0);
    }
  }, [splitComponents, layoutInitialized]);

  return (
    <div className="space-y-4 p-4 md:p-6">
      <div className="mb-4">
        <div className="bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg p-3 md:p-4 space-y-2 md:space-y-3">
          <p className="text-xs md:text-sm text-gray-700 dark:text-gray-200">
            Network visualization showing <strong>direct (1-hop) connections</strong>{" "}
            between genes in the selected pathway. Genes appear as interactive
            nodes, with edges representing the chosen relationship type.{" "}
            <strong>Disconnected genes</strong> indicate the absence of direct
            links, but indirect connections through intermediate genes may exist
            (explore these using the K-Shortest Paths section below).
          </p>

          <div className="pt-2 border-t border-gray-200 dark:border-slate-600">
            <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">
              The INTERACTS_WITH relation aggregates interactions from{" "}
              <span className="font-medium text-blue-600 dark:text-blue-400">
                STRING
              </span>{" "}
              and{" "}
              <span className="font-medium text-green-600 dark:text-green-400">
                HUBBLE
              </span>{" "}
              databases. HUBBLE interactions derive from literature mining. This
              relation type is undirected (bidirectional, no arrows).
            </p>
          </div>
          <div className="pt-2 border-t border-gray-200 dark:border-slate-600">
            <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">
              <i>
                Note that this relation type is undirected. Undirected relations
                are bidirectional and shown without arrows. Directed relations
                show edge direction with arrows both in visualizations and path
                tables.
              </i>
            </p>
          </div>
        </div>
      </div>

      {networkData && networkData.nodes.length > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
            Direct neighbor edges ({networkData.edges.length})
          </h4>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">View:</span>
            <div className="flex rounded-lg border border-gray-200 dark:border-slate-600 overflow-hidden bg-gray-50 dark:bg-slate-700">
              <button
                type="button"
                onClick={() => setViewMode("graph")}
                className={`px-3 py-1.5 text-sm font-medium transition-colors ${viewMode === "graph"
                  ? "bg-white dark:bg-slate-600 text-gray-900 dark:text-gray-100 shadow-sm"
                  : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                  }`}
              >
                Network graph
              </button>
              <button
                type="button"
                onClick={() => {
                  layoutInitialized.current = false;
                  setViewMode("table");
                }}
                className={`px-3 py-1.5 text-sm font-medium transition-colors border-l border-gray-200 dark:border-slate-600 ${viewMode === "table"
                  ? "bg-white dark:bg-slate-600 text-gray-900 dark:text-gray-100 shadow-sm"
                  : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                  }`}
              >
                Path table
              </button>
            </div>
          </div>
        </div>
      )}

      {viewMode === "table" && networkData ? (
        <div
          ref={edgeTableFullscreenRef}
          className={`relative rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 overflow-hidden min-w-0 max-w-full ${isEdgeTableFullscreen ? "border-0" : "w-full"}`}
          style={{
            width: isEdgeTableFullscreen ? "100vw" : undefined,
            height: isEdgeTableFullscreen ? "100vh" : undefined,
            minHeight: isEdgeTableFullscreen ? "100vh" : undefined,
          }}
        >
          <div className="flex flex-wrap items-center justify-end gap-2 p-2 border-b border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-700/50 min-w-0 shrink-0">
            <div className="relative min-w-[180px] max-w-sm">
              <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-slate-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Search table..."
                value={edgeTableSearchQuery}
                onChange={(e) => setEdgeTableSearchQuery(e.target.value)}
                className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 dark:border-slate-500 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <button
              type="button"
              onClick={handleDownloadEdgeTableCsv}
              title="Download as CSV"
              className="p-2 rounded-lg border border-gray-300 dark:border-slate-500 bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={handleEdgeTableFullscreen}
              title={isEdgeTableFullscreen ? "Exit fullscreen" : "Fullscreen"}
              className="p-2 rounded-lg border border-gray-300 dark:border-slate-500 bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors"
            >
              {isEdgeTableFullscreen ? (
                <ArrowsPointingInIcon className="h-4 w-4" />
              ) : (
                <ArrowsPointingOutIcon className="h-4 w-4" />
              )}
            </button>
          </div>
          <div className="overflow-x-auto overflow-y-auto min-w-0" style={isEdgeTableFullscreen ? { height: "calc(100vh - 52px)" } : undefined}>
            <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-600">
              <thead className="bg-gray-50 dark:bg-slate-700 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                    Source
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                    Target
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                    Path
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                    Path length
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-slate-600">
                {filteredEdgeRows.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                      {edgeTableSearchQuery.trim() ? `No rows match "${edgeTableSearchQuery.trim()}".` : "No edge data."}
                    </td>
                  </tr>
                ) : (
                  filteredEdgeRows.map((e, i) => (
                    <tr key={`${e.source}-${e.target}-${i}`}>
                      <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">
                        {e.source}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">
                        {e.target}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">
                        {Array.isArray(e.path) ? e.path.join(" → ") : ""}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">
                        {e.path_length ?? ""}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : viewMode === "graph" && networkData && networkData.nodes.length > 0 ? (
        <div
          ref={containerRef}
          tabIndex={-1}
          className={`relative ${pathwayExplorerStyles.networkContainer} outline-none ${isFullscreen ? "bg-white dark:bg-slate-900" : ""}`}
          style={{
            overflow: "hidden",
            minHeight: isFullscreen ? "100vh" : "600px",
            width: "100%",
            position: "relative",
          }}
          aria-label="Network graph"
        >
          <div
            className={`bg-slate-50 dark:bg-slate-800/95 border border-gray-200 dark:border-slate-700 ${isPlotControlsOpen ? "border-r-0 rounded-l-lg" : "rounded-lg"
              } overflow-hidden absolute inset-0 transition-all duration-300`}
            style={{ right: isPlotControlsOpen ? "320px" : "0" }}
          >
            {networkData && (
              <div className="absolute top-2 left-2 z-30 flex flex-col gap-1">
                {selectedPathwayLabel && (
                  <div className="bg-black bg-opacity-70 text-white text-xs font-medium px-2 py-1.5 rounded max-w-[min(320px,70vw)] truncate pointer-events-none" title={selectedPathwayLabel}>
                    {selectedPathwayLabel}
                  </div>
                )}
                {selectedNodeLabel != null && (
                  <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg shadow-lg max-w-[min(320px,70vw)] overflow-hidden pointer-events-auto">
                    <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-gray-100 dark:border-slate-700">
                      <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                        Gene: {selectedNodeLabel}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedNodeLabel(null);
                          setConnectedGenes([]);
                          setTapFocusNodeId(null);
                          setTapNeighborIds([]);
                        }}
                        className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-600 text-gray-500 dark:text-gray-400"
                        aria-label="Close"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="px-3 py-2 max-h-40 overflow-y-auto">
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                        Connected genes ({connectedGenes.length}):
                      </p>
                      {connectedGenes.length === 0 ? (
                        <p className="text-xs text-gray-500 dark:text-gray-400 italic">None</p>
                      ) : (
                        <p className="text-xs text-gray-700 dark:text-gray-200 break-words">
                          {connectedGenes.join(", ")}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {networkData && networkData.nodes.length > 0 && (
              <div className="absolute top-2 right-2 z-30 flex items-center gap-1">
                <button
                  onClick={handleReset}
                  title="Reset layout"
                  className="p-1.5 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-md shadow-sm hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors"
                >
                  <ArrowPathIcon className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                </button>
                <button
                  onClick={handleDownload}
                  title="Download as PNG"
                  className="p-1.5 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-md shadow-sm hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors"
                >
                  <ArrowDownTrayIcon className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                </button>
                <button
                  onClick={handleFullscreen}
                  title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
                  className="p-1.5 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-md shadow-sm hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors"
                >
                  {isFullscreen ? (
                    <ArrowsPointingInIcon className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                  ) : (
                    <ArrowsPointingOutIcon className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                  )}
                </button>
              </div>
            )}

            {networkData ? (
              <>
                {networkData.nodes.length > 0 ? (
                  <div className="w-full h-full flex flex-col relative">
                    {splitComponents && components.length > 1 && (
                      <div className="flex items-center justify-between px-6 py-4 bg-white dark:bg-slate-800 z-10 mt-8">
                        <button
                          onClick={() => setActivePlotIndex(Math.max(0, activePlotIndex - 1))}
                          disabled={activePlotIndex === 0}
                          className={`p-2 rounded-lg border ${activePlotIndex === 0
                            ? "bg-gray-100 dark:bg-slate-700 text-gray-400 dark:text-gray-500 border-gray-200 dark:border-slate-600 cursor-not-allowed"
                            : "bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-600 shadow-sm"
                            } transition-colors`}
                        >
                          <ChevronLeftIcon className="w-5 h-5" />
                        </button>
                        <div className="text-center">
                          <h3 className="text-lg md:text-xl font-semibold text-gray-800 dark:text-gray-100 flex items-center justify-center gap-2">
                            {activePlotIndex === 0 ? "Largest Connected Component" : "Disconnected Genes"}
                            <span className="text-gray-500 dark:text-gray-400 font-normal text-base">
                              (Plot {activePlotIndex + 1} of {components.length})
                            </span>
                          </h3>
                          <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 mt-1">
                            {activePlotIndex === 0 ? "Largest Connected Component" : "Other Genes"}
                          </p>
                        </div>
                        <button
                          onClick={() => setActivePlotIndex(Math.min(components.length - 1, activePlotIndex + 1))}
                          disabled={activePlotIndex === components.length - 1}
                          className={`p-2 rounded-lg border ${activePlotIndex === components.length - 1
                            ? "bg-gray-100 dark:bg-slate-700 text-gray-400 dark:text-gray-500 border-gray-200 dark:border-slate-600 cursor-not-allowed"
                            : "bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-600 shadow-sm"
                            } transition-colors`}
                        >
                          <ChevronRightIcon className="w-5 h-5" />
                        </button>
                      </div>
                    )}

                    <div className="relative flex-grow w-full">
                      {components.map((compElements, idx) => (
                        <div
                          key={splitComponents ? `comp-${idx}` : "all"}
                          className="absolute inset-0 w-full h-full"
                          style={{
                            visibility: (!splitComponents || idx === activePlotIndex) ? "visible" : "hidden",
                            pointerEvents: (!splitComponents || idx === activePlotIndex) ? "auto" : "none",
                            zIndex: (!splitComponents || idx === activePlotIndex) ? 10 : 0,
                          }}
                        >
                          <CytoscapeComponent
                            key={`graph-${compElements.length}-${compElements[0]?.data.id ?? ""}-${splitComponents}`}
                            elements={compElements}
                            style={{ width: "100%", height: "100%" }}
                            stylesheet={cytoscapeStylesheet}
                            cy={(cy: Core) => {
                              if (idx === 0) cyRef.current = cy;

                              cy.nodes().ungrabify();
                              cy.nodes().grabify();

                              if (!layoutInitialized.current || splitComponents) {
                                cy.on("tap", (evt: any) => {
                                  if (evt.target === cy) {
                                    setTapFocusNodeId(null);
                                    setTapNeighborIds([]);
                                    setSelectedNodeLabel(null);
                                    setConnectedGenes([]);
                                  }
                                });
                                cy.on("tap", "node", (evt) => {
                                  const node = evt.target;
                                  const label = node.data("label");
                                  const nid = node.id();
                                  if (selectedNodeLabelRef.current === label) {
                                    setSelectedNodeLabel(null);
                                    setConnectedGenes([]);
                                    setTapFocusNodeId(null);
                                    setTapNeighborIds([]);
                                    return;
                                  }
                                  const neighbors = node.neighborhood("node");
                                  const neighborIds = neighbors.map((n: any) => n.id() as string);
                                  setTapFocusNodeId(nid);
                                  setTapNeighborIds(neighborIds);
                                  const labels = neighbors.map((n: any) => n.data("label")).filter(Boolean);
                                  setSelectedNodeLabel(label);
                                  setConnectedGenes(labels);
                                });

                                cy.on("tap", "edge", (evt) => {
                                  const edge = evt.target;
                                  console.log("Clicked interaction:", edge.data());
                                });

                                cy.on("grab", "node", (evt) => {
                                  const node = evt.target;
                                  node.addClass("dragging");
                                  console.log("Dragging gene:", node.data("label"));
                                });

                                cy.on("free", "node", (evt) => {
                                  const node = evt.target;
                                  node.removeClass("dragging");
                                  console.log(
                                    "Dropped gene:",
                                    node.data("label"),
                                    "at position:",
                                    node.position()
                                  );
                                });

                                const layout = cy.layout({
                                  name: "cose",
                                  animate: true,
                                  animationDuration: 1000,
                                  nodeDimensionsIncludeLabels: true,
                                  idealEdgeLength: 60,
                                  gravity: 1,
                                  gravityRange: 2.5,
                                } as any);

                                layout.run();

                                layout.on("layoutstop", () => {
                                  if (idx === 0) layoutInitialized.current = true;
                                  cy.fit(undefined, 30);

                                  // Sync computed font size to plot controls only once per graph load (don't overwrite user changes)
                                  if (idx === 0 && !hasSyncedInitialFontSizeRef.current) {
                                    hasSyncedInitialFontSizeRef.current = true;
                                    const nodeCount = cy.nodes().length;
                                    const container = cy.container();
                                    const w = container?.clientWidth ?? 600;
                                    const h = container?.clientHeight ?? 400;
                                    const areaPerNode = (w * h) / Math.max(nodeCount, 1);
                                    const computedFontSize = Math.round(
                                      Math.max(9, Math.min(14, 5 + Math.sqrt(areaPerNode) / 25))
                                    );
                                    setLabelFontSize(computedFontSize);
                                  }
                                });
                              }
                            }}
                            wheelSensitivity={0.2}
                            autoungrabify={false}
                            autounselectify={false}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <div className="text-2xl mb-4">⚠️</div>
                      <h4 className="text-lg font-medium text-gray-500 mb-2">
                        No Nodes Found
                      </h4>
                      <p className="text-sm text-gray-400">
                        Network data exists but contains no nodes to display
                      </p>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <MapIcon className="h-16 w-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                  <h4 className="text-lg font-medium text-gray-500 dark:text-gray-400 mb-2">
                    No Network Data
                  </h4>
                  <p className="text-sm text-gray-400 dark:text-gray-500 mb-4">
                    Click "Analyze" above to generate and visualize the pathway
                    network
                  </p>
                </div>
              </div>
            )}
          </div>

          {networkData && (
            <button
              onClick={() => setIsPlotControlsOpen(!isPlotControlsOpen)}
              className="absolute top-1/2 -translate-y-1/2 z-40 flex items-center justify-center w-7 h-16 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 active:from-blue-800 active:to-blue-900 text-white shadow-lg hover:shadow-xl transition-all duration-300 group rounded-l-md"
              style={{ right: isPlotControlsOpen ? "320px" : "0" }}
              title={isPlotControlsOpen ? "Hide controls" : "Show controls"}
            >
              {isPlotControlsOpen ? (
                <ChevronUpIcon className="h-4 w-4 transform rotate-90 group-hover:-translate-x-0.5 transition-transform duration-200" />
              ) : (
                <ChevronDownIcon className="h-4 w-4 transform rotate-90 group-hover:translate-x-0.5 transition-transform duration-200" />
              )}
            </button>
          )}

          <PlotControlsDrawer
            isPlotControlsOpen={isPlotControlsOpen}
            networkData={networkData}
            selectedGeneToHighlight={selectedGeneToHighlight}
            setSelectedGeneToHighlight={setSelectedGeneToHighlight}
            highlightColumn={highlightColumn}
            setHighlightColumn={setHighlightColumn}
            datasetId={datasetId}
            labelFontSize={labelFontSize}
            setLabelFontSize={setLabelFontSize}
            splitComponents={splitComponents}
            setSplitComponents={setSplitComponents!}
          />
        </div>
      ) : null}
    </div>
  );
};
